import React, { useMemo } from 'react';
import { addDays, format, parse, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  IconCalendarTime,
  IconCalendarEvent,
  IconClock,
} from '@tabler/icons-react';
import { useProfile } from '@shared/api/useProfile';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { useChatStore } from '@entities/messanger';
import { useTeacherTimezone, useTeacherInstruments } from '../api/use-teacher-timezone';
import { convertSlotDateTime } from '../lib/convert-slot-time';
import { OfferCard } from './offer-card';
import s from './interactive-message.module.scss';

const BOOKING_ACTION_KEY = 'book';

// Бэк кладёт инструмент в заголовок ("Предложение слота: Фортепиано") —
// показываем заголовок без него, инструмент выводим отдельным чипом.
const stripInstrumentFromTitle = (title) => {
  if (!title || !title.includes(':')) return title;
  return title.split(':')[0].trim();
};

// Минимальная дата бронирования — завтра (как на странице бронирования:
// startOfDay(addDays(new Date(), 1))). Считаем в локальном времени
// браузера, чтобы правило совпадало со страницей бронирования.
const getMinBookableDate = () => startOfDay(addDays(new Date(), 1));

const isSlotBookable = (converted) => {
  if (!converted) return true; // не можем оценить — не блокируем кнопку
  const slotDate = parse(converted.date, 'dd.MM.yyyy', new Date());
  return slotDate.getTime() >= getMinBookableDate().getTime();
};

// Карточка предложения слота в чате.
// Информация одинаковая для преподавателя и ученика: детали считаем из
// payload, время конвертируем из таймзоны преподавателя в таймзону зрителя
// (для самого преподавателя конвертация тождественная). Отличаются только
// кнопки — их бэк присылает только получателю. Кнопка бронирования неактивна,
// если слот уже нельзя забронировать (минимум — на завтра).
export const SlotOfferEntityCard = ({ message }) => {
  const { data: profile } = useProfile();
  // participants[0] — преподаватель только в чате ученика. В чате самого
  // преподавателя первый участник — ученик (teacher_slug у него нет), поэтому
  // для преподавателя берём slug из его профиля — конвертация тождественная.
  const participantSlug = useChatStore(
    (state) =>
      state.chats.find((c) => c.id === message.chat_id)?.participants?.[0]
        ?.teacher_slug
  );
  const teacherSlug =
    profile?.role === 'teacher' ? profile?.teacher_slug : participantSlug;
  const { data: teacherTimezone } = useTeacherTimezone(teacherSlug);
  const { data: teacherInstruments } = useTeacherInstruments(teacherSlug);

  const payload = message.payload || {};

  const instrumentName =
    payload.instrument_name ||
    payload.instrument ||
    teacherInstruments?.find(
      (i) => String(i.id) === String(payload.instrument_id)
    )?.name;

  const converted = useMemo(
    () =>
      convertSlotDateTime(
        payload.date,
        payload.time,
        teacherTimezone,
        profile?.timezone
      ),
    [payload.date, payload.time, teacherTimezone, profile?.timezone]
  );

  const canBook = useMemo(() => isSlotBookable(converted), [converted]);

  const card = message.card || {};
  const title = stripInstrumentFromTitle(card.title) || 'Предложение занятия';

  // Дата для чипа: "d MMMM" из сконвертированной даты (дата без времени —
  // таймзона на неё не влияет)
  const dateLabel = converted
    ? format(parse(converted.date, 'dd.MM.yyyy', new Date()), 'd MMMM', {
        locale: ru,
      })
    : null;

  const actions = useMemo(
    () =>
      (card.actions || []).map((action) =>
        action.key === BOOKING_ACTION_KEY && !canBook
          ? {
              ...action,
              disabled: true,
              disabledReason: 'Запись на эту дату недоступна',
            }
          : action
      ),
    [card.actions, canBook]
  );

  return (
    <OfferCard
      icon={<IconCalendarTime size={20} />}
      title={title}
      status={card.status}
      actions={actions}
      message={message}
    >
      {converted ? (
        <div className={s.chips}>
          <span className={s.chip}>
            <IconCalendarEvent size={14} />
            {dateLabel}
          </span>
          <span className={s.chip}>
            <IconClock size={14} />
            {converted.time}
          </span>
          {instrumentName && (
            <span className={s.chip}>
              <TextWithEmoji text={instrumentName} />
            </span>
          )}
        </div>
      ) : (
        // Не хватило данных для конвертации — показываем строки от бэка,
        // а если и их нет — сырые чипы из payload, чтобы карточка не была пустой
        Array.isArray(card.lines) && card.lines.length > 0 ? (
          <div className={s.lines}>
            {card.lines.map((line, index) => (
              <span key={index} className={s.line}>
                {line}
              </span>
            ))}
          </div>
        ) : (
          (payload.date || payload.time || instrumentName) && (
            <div className={s.chips}>
              {payload.date && (
                <span className={s.chip}>
                  <IconCalendarEvent size={14} />
                  {payload.date}
                </span>
              )}
              {payload.time && (
                <span className={s.chip}>
                  <IconClock size={14} />
                  {payload.time}
                </span>
              )}
              {instrumentName && (
                <span className={s.chip}>
                  <TextWithEmoji text={instrumentName} />
                </span>
              )}
            </div>
          )
        )
      )}
      {!canBook && (
        <span className={s.statusText}>Запись на эту дату недоступна</span>
      )}
    </OfferCard>
  );
};
